import { Controller } from "@hotwired/stimulus"
import { csrfToken } from "../utils/csrf"
import { announceTaskAttachments, dragHasFiles, uploadTaskAttachments } from "../utils/task_attachments"

// The Files panel for one task (task_attachments/_panel), in the outline's
// detail sidebar. Like the comment thread it talks to the page only through
// window events (see utils/task_attachments.js):
//
//   jane:task-attachments-count   { taskId, count, html }  a write happened -
//                                 from here or a row - swap in the new list
//   jane:task-attachment-changed  { taskId }  someone else wrote (the outline
//                                 relays TaskListChannel attachment.changed)
//                                 -> re-fetch the list
export default class extends Controller {
    static targets = ["list", "input", "drop", "count", "error", "progress"]
    static values = { url: String, taskId: Number, maxBytes: Number }

    connect() {
        this._onCount = (event) => {
            const { taskId, count, html } = event.detail || {}
            if (Number(taskId) !== this.taskIdValue) return
            this._setCount(count)
            if (html != null) this.listTarget.innerHTML = html
        }
        this._onRemoteChange = (event) => {
            if (Number(event.detail?.taskId) === this.taskIdValue) this.refresh()
        }
        window.addEventListener("jane:task-attachments-count", this._onCount)
        window.addEventListener("jane:task-attachment-changed", this._onRemoteChange)
    }

    disconnect() {
        window.removeEventListener("jane:task-attachments-count", this._onCount)
        window.removeEventListener("jane:task-attachment-changed", this._onRemoteChange)
    }

    // The "add a file" link in the list's own text: the input sits outside the
    // list, which every upload re-renders.
    pick() {
        if (this.hasInputTarget) this.inputTarget.click()
    }

    choose() {
        const files = Array.from(this.inputTarget.files || [])
        this.inputTarget.value = ""
        this._upload(files)
    }

    dragOver(e) {
        if (!dragHasFiles(e)) return
        e.preventDefault()
        e.dataTransfer.dropEffect = "copy"
        if (this.hasDropTarget) this.dropTarget.classList.add("is-over")
    }

    dragLeave(e) {
        if (this.element.contains(e.relatedTarget)) return
        if (this.hasDropTarget) this.dropTarget.classList.remove("is-over")
    }

    drop(e) {
        if (!dragHasFiles(e)) return
        e.preventDefault()
        if (this.hasDropTarget) this.dropTarget.classList.remove("is-over")
        this._upload(Array.from(e.dataTransfer.files || []))
    }

    remove(e) {
        const item = e.target.closest("[data-task-attachment]")
        const name = item?.querySelector(".task-attachment-name")?.textContent?.trim() || "this file"
        if (!item || !window.confirm(`Remove ${name}?`)) return
        const headers = { Accept: "application/json", "X-CSRF-Token": csrfToken() }
        const clientId = document.querySelector("[data-task-list-client-id]")?.dataset.taskListClientId
        if (clientId) headers["X-Task-List-Client-Id"] = clientId
        fetch(`${this.urlValue}/${item.dataset.attachmentId}`, { method: "DELETE", headers, credentials: "same-origin" })
            .then((r) => r.json())
            .catch(() => null)
            .then((data) => {
                if (!data?.ok) return this._showError(data?.error || "Could not remove the file")
                item.remove()
                announceTaskAttachments(this.taskIdValue, data.count)
                if (!this.listTarget.querySelector("[data-task-attachment]")) this.refresh()
            })
    }

    refresh() {
        fetch(`${this.urlValue}?part=list`, { headers: { Accept: "text/html" }, credentials: "same-origin" })
            .then((r) => (r.ok ? r.text() : null))
            .then((html) => {
                if (html == null) return
                this.listTarget.innerHTML = html
                this._setCount(this.listTarget.querySelectorAll("[data-task-attachment]").length)
            })
            .catch(() => {})
    }

    _upload(files) {
        if (!files.length) return
        if (this.hasProgressTarget) this.progressTarget.hidden = false
        uploadTaskAttachments(this.taskIdValue, files, { maxBytes: this.maxBytesValue || undefined }).then((data) => {
            if (this.hasProgressTarget) this.progressTarget.hidden = true
            if (data?.error) this._showError(data.error)
        })
    }

    _setCount(count) {
        // The task pane's folded empty Files stops saying "None yet".
        const section = count ? this.listTarget.closest("details.is-empty") : null
        if (section) {
            section.classList.remove("is-empty")
            section.querySelector(".task-pane-eyebrow-hint")?.remove()
        }
        if (!this.hasCountTarget) return
        this.countTarget.textContent = String(count)
        this.countTarget.hidden = !count
    }

    _showError(message) {
        if (!this.hasErrorTarget) return window.alert(message)
        this.errorTarget.textContent = message
        this.errorTarget.hidden = false
        clearTimeout(this._errorTimer)
        this._errorTimer = setTimeout(() => { this.errorTarget.hidden = true }, 6000)
    }
}
